import { Box, Chip, Skeleton, Typography, useTheme } from '@mui/material';
import CustomCard from 'components/shared/CustomCard/CustomCard';
import { LightTooltip } from 'components/shared/CustomTooltip/CustomTooltip';
import { ClientType } from 'model/client.model';
import zeplinColor from 'theme/Color';

const CustomClientCard = ({ client, loading }: { client: ClientType; loading: boolean }) => {
  const theme = useTheme();
  return (
    <>
      <CustomCard
        styles={{
          borderRadius: '14px',
          border: `1px solid ${zeplinColor.Neutral5}`,
          '&:hover': {
            borderColor: zeplinColor.BasicBlack,
          },
          padding: '16px',
          gap: '12px',
          display: 'flex',
          flexDirection: 'column',
          minWidth: '277px',
          cursor: 'pointer',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
          {client?.avatar ? (
            <img
              src={client?.avatar}
              alt='client-avatar'
              style={{
                position: 'relative',
                objectFit: 'fill',
                height: '82px',
                borderRadius: '14px 14px 14px 14px',
                border: `1px solid ${zeplinColor.Neutral3}`,
                width: '82px',
              }}
            />
          ) : (
            <Box
              sx={{
                backgroundColor:
                  client?.category === 'Talent'
                    ? zeplinColor.Brand1
                    : loading
                      ? zeplinColor.Neutral3
                      : client?.category === 'Athlete'
                        ? zeplinColor.USER_PROFILE_COLOR1
                        : client?.category === 'Business'
                          ? zeplinColor.USER_PROFILE_COLOR2
                          : client?.category === 'Business_Development'
                            ? zeplinColor.USER_PROFILE_COLOR3
                            : zeplinColor.Brand1,
                borderRadius: '14px 14px 14px 14px',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minWidth: '82px',
                maxWidth: '82px',
                height: '82px',
              }}
            >
              <Typography
                variant='display4'
                sx={{
                  color: zeplinColor.Neutral1,
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                }}
              >
                {client?.Name?.split(' ').map((val, index) =>
                  index === 0 || index === client?.Name?.split(' ').length - 1 ? val[0] : '',
                )}
              </Typography>
            </Box>
          )}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '16px',
              flex: 1,
              overflow: 'hidden',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,
                }}
              />
            ) : (
              <Box
                sx={{
                  width: '100%',
                  maxWidth: '100%',
                }}
              >
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      {client?.Name || 'N/A'}
                    </Typography>
                  }
                  placement='bottom-start'
                  style={{
                    backgroundColor: zeplinColor.BasicBlack,
                    borderRadius: '8px',
                    position: 'relative',
                  }}
                >
                  <Typography
                    variant='body1'
                    sx={{
                      whiteSpace: 'nowrap',
                      overflow: 'hidden',
                      maxWidth: 'calc(100% - 10px)',
                      textOverflow: 'ellipsis',
                      width: '100%',
                      boxSizing: 'border-box',
                    }}
                  >
                    {client?.Name || 'N/A'}
                  </Typography>
                </LightTooltip>
              </Box>
            )}
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                  flex: 1,
                }}
              />
            ) : (
              <Box sx={{ display: 'inline-flex', gap: '4px', width: '100%' }}>
                <LightTooltip
                  title={
                    <Typography variant='body3' color={zeplinColor.Neutral1}>
                      {client?.category === 'Business_Development'
                        ? 'Business Development'
                        : client.category || 'NA'}
                    </Typography>
                  }
                  placement='bottom-start'
                  style={{
                    backgroundColor: zeplinColor.BasicBlack,
                    borderRadius: '8px',
                    position: 'relative',
                  }}
                >
                  <Chip
                    sx={{
                      backgroundColor: zeplinColor.BasicBlack,
                      borderRadius: '50px',
                      height: '24px',
                      padding: '5px 12px',
                      overflow: 'hidden',
                    }}
                    label={
                      <Typography
                        variant='label1'
                        color={zeplinColor.Neutral1}
                        sx={{
                          textAlign: 'center',
                          display: 'block',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {client?.category === 'Business_Development'
                          ? 'Business Development'
                          : client.category || 'NA'}
                      </Typography>
                    }
                  />
                </LightTooltip>
              </Box>
            )}
          </Box>
        </Box>
        <Box sx={{ display: 'flex', gap: '12px' }}>
          <Box
            sx={{
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: zeplinColor.Neutral3,
              flex: 1,
              textAlign: 'center',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                }}
              />
            ) : (
              <Typography variant={'body2'}>${client?.openDealAmount || 'N/A'}</Typography>
            )}
            <Typography variant={'label1'} color={zeplinColor.Neutral8}>
              Open Deals
            </Typography>
          </Box>
          <Box
            sx={{
              borderRadius: '8px',
              padding: '8px',
              display: 'flex',
              flexDirection: 'column',
              gap: '8px',
              backgroundColor: zeplinColor.Neutral3,
              flex: 1,
              textAlign: 'center',
            }}
          >
            {loading ? (
              <Skeleton
                variant='text'
                sx={{
                  fontSize: theme.typography.body2,
                }}
              />
            ) : (
              <Typography variant={'body2'}>${client?.closedDealAmount || 'N/A'}</Typography>
            )}
            <Typography variant={'label1'} color={zeplinColor.Neutral8}>
              Close Deals
            </Typography>
          </Box>
        </Box>
      </CustomCard>
    </>
  );
};

export default CustomClientCard;
